import React, { useState, useEffect } from 'react';
import { useAppContext } from '../contexts/AppContext';
import ExternalMaterialSearch from '../components/ExternalMaterialSearch';
import { MaterialSpecification } from '../types';
import {
    Upload,
    Download,
    Search,
    Plus,
    Edit,
    Trash2,
    FileText,
    Link as LinkIcon,
    Database,
    X
} from 'lucide-react';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';
import { downloadCSVWithAudit, ExportAuditMetadata } from '../utils/exportUtils';

interface ExtendedMaterialSpec extends MaterialSpecification {
    validationStatus?: 'validated' | 'unvalidated' | 'partial';
    generation?: number;
    testDataIds?: string[];
    testsCompleted?: string[];
    testsRequired?: string[];
    cost?: { value: number; currency: string };
    leadTimeWeeks?: number;
    reliability?: any;
}

export default function MaterialsDatabase() {
    const { allData, activeProject, currentUser } = useAppContext();
    const [materials, setMaterials] = useState<ExtendedMaterialSpec[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState<string>('all');
    const [showImporter, setShowImporter] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState<ExtendedMaterialSpec | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [activeTab, setActiveTab] = useState<'local' | 'external'>('local');

    // Project filtering helper
    const isItemInProject = (projName?: string) => {
        if (!activeProject) return true; // Show all if no filter
        if (!projName) return true; // Global library item
        if (projName === activeProject) return true;

        // Match project names from epoch titles if possible
        if (activeProject.includes(projName) || projName.includes(activeProject)) return true;

        const projectName = activeProject.split(' - ')[0];
        if (projName === projectName) return true;

        return false;
    };

    useEffect(() => {
        // ✅ Load materials from backend data only
        if (allData.materials && allData.materials.length > 0) {
            console.log('📦 Loading materials from backend:', allData.materials.length);
            setMaterials(allData.materials as any[]);
        } else {
            console.log('📦 No materials in backend, starting empty');
            setMaterials([]);
        }
    }, [allData.materials]);

    // ✅ Materials are part of project data (allData.materials)
    // Changes are saved to backend when user clicks "Save Active Project"
    // This component just manages local editing state
    const saveMaterialsToBackend = (mats: ExtendedMaterialSpec[]) => {
        setMaterials(mats);
        console.log('📝 Materials updated. Click "Save Active Project" to persist to backend.');
    };

    const addMaterial = (material: ExtendedMaterialSpec) => {
        const newMaterials = [...materials, material];
        saveMaterialsToBackend(newMaterials);
        setShowAddModal(false);
    };

    const deleteMaterial = (id: string) => {
        if (confirm('Are you sure you want to delete this material?')) {
            const newMaterials = materials.filter(m => m.id !== id);
            saveMaterialsToBackend(newMaterials);
        }
    };

    const exportMaterials = () => {
        const headers = ['ID', 'Name', 'Grade', 'Standard', 'Supplier', 'Validation', 'Cost'].join(',');
        const rows = materials.map(m => [
            m.id,
            m.materialName,
            m.grade,
            m.standard,
            m.supplier || '',
            m.validationStatus || '',
            m.cost?.value || ''
        ].join(','));

        const metadata: ExportAuditMetadata = {
            exporterUid: currentUser?.uid || 'unknown',
            organization: (allData.projectMetadata as any)?.organization || 'unknown',
            timestamp: new Date().toISOString()
        };

        downloadCSVWithAudit(
            `materials_export_${new Date().toISOString().split('T')[0]}.csv`,
            headers,
            rows,
            metadata
        );
    };

    const categories = ['all', ...Array.from(new Set(materials.map(m =>
        m.materialName.split(' ')[0].toLowerCase()
    )))];

    const filteredMaterials = materials.filter(m => {
        const matchesSearch = m.materialName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.grade.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' ||
            m.materialName.toLowerCase().startsWith(filterCategory);
        const matchesProject = isItemInProject((m as any).projectName);
        return matchesSearch && matchesCategory && matchesProject;
    });

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'validated':
                return 'bg-green-100 text-green-800 border-green-300';
            case 'unvalidated':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'partial':
                return 'bg-orange-100 text-orange-800 border-orange-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                            <Database className="w-8 h-8 text-primary" />
                            Materials Database
                        </h1>
                        <p className="text-text-secondary mt-2">
                            Manage material specifications and validation data
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={exportMaterials}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Export
                        </button>
                        <button
                            onClick={() => setShowImporter(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                            <Upload className="w-4 h-4" />
                            Import
                        </button>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-secondary text-white rounded-lg transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Add Material
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">Total Materials</div>
                        <div className="text-2xl font-bold text-text-primary">{materials.length}</div>
                    </div>
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">Validated</div>
                        <div className="text-2xl font-bold text-green-600">
                            {materials.filter(m => m.validationStatus === 'validated').length}
                        </div>
                    </div>
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">Unvalidated</div>
                        <div className="text-2xl font-bold text-yellow-600">
                            {materials.filter(m => m.validationStatus === 'unvalidated').length}
                        </div>
                    </div>
                    <div className="bg-background p-4 rounded-lg border border-border">
                        <div className="text-sm text-text-secondary">Categories</div>
                        <div className="text-2xl font-bold text-text-primary">{categories.length - 1}</div>
                    </div>
                </div>
                
                {/* Tabs */}
                <div className="flex space-x-4 border-b border-border mt-4">
                    <button
                        onClick={() => setActiveTab('local')}
                        className={`pb-2 px-1 text-sm font-medium transition-colors ${activeTab === 'local' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary hover:text-text-primary'}`}
                    >
                        Local Database
                    </button>
                    <button
                        onClick={() => setActiveTab('external')}
                        className={`pb-2 px-1 text-sm font-medium transition-colors ${activeTab === 'external' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary hover:text-text-primary'}`}
                    >
                        External Search (MatWeb)
                    </button>
                </div>
            </div>

            {activeTab === 'external' ? (
                <ExternalMaterialSearch />
            ) : (
                <>
                    {/* Filters */}
            <div className="bg-card rounded-lg shadow-md p-4 border border-border">
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                        <label className="text-sm text-text-secondary mb-2 block">Search Materials</label>
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name or grade..."
                                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm text-text-secondary mb-2 block">Filter by Category</label>
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        >
                            {categories.map(cat => (
                                <option key={cat} value={cat}>
                                    {cat === 'all' ? 'All Materials' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Materials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMaterials.map((material) => (
                    <div
                        key={material.id}
                        className="bg-card rounded-lg shadow-md p-4 border border-border hover:border-primary transition-all cursor-pointer"
                        onClick={() => setSelectedMaterial(material)}
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                                <h3 className="font-semibold text-lg text-text-primary">{material.materialName}</h3>
                                <p className="text-sm text-text-secondary">{material.id}</p>
                            </div>
                            {material.validationStatus && (
                                <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(material.validationStatus)}`}>
                                    {material.validationStatus}
                                </span>
                            )}
                        </div>

                        {/* Details */}
                        <div className="space-y-2">
                            <div className="text-sm">
                                <span className="text-text-secondary">Grade:</span>
                                <span className="ml-2 text-text-primary font-medium">{material.grade}</span>
                            </div>
                            <div className="text-sm">
                                <span className="text-text-secondary">Standard:</span>
                                <span className="ml-2 text-text-primary font-medium">{material.standard}</span>
                            </div>
                            {material.supplier && (
                                <div className="text-sm">
                                    <span className="text-text-secondary">Supplier:</span>
                                    <span className="ml-2 text-text-primary font-medium">{material.supplier}</span>
                                </div>
                            )}
                            {material.cost && (
                                <div className="text-sm">
                                    <span className="text-text-secondary">Cost:</span>
                                    <span className="ml-2 text-green-600 font-semibold">
                                        {material.cost.value} {material.cost.currency}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Properties Preview */}
                        <div className="mt-3 pt-3 border-t border-border">
                            <div className="text-xs text-text-secondary mb-1">Properties:</div>
                            <div className="text-xs space-y-1">
                                {Object.entries(material.properties).slice(0, 2).map(([key, val]) => (
                                    <div key={key}>
                                        <span className="text-text-secondary">{key}:</span>
                                        <span className="ml-2 text-text-primary">{val}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedMaterial(material);
                                }}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded transition-colors"
                            >
                                <Edit className="w-3 h-3" />
                                Edit
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    deleteMaterial(material.id);
                                }}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-red-50 hover:bg-red-100 text-red-700 rounded transition-colors"
                            >
                                <Trash2 className="w-3 h-3" />
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Import Modal */}
            {showImporter && (
                <MaterialImporter
                    onClose={() => setShowImporter(false)}
                    onImport={(imported) => {
                        const newMaterials = [...materials, ...imported];
                        saveMaterialsToBackend(newMaterials);
                        setShowImporter(false);
                    }}
                />
            )}

            {/* Material Detail Modal */}
            {selectedMaterial && (
                <MaterialDetailModal
                    material={selectedMaterial}
                    onClose={() => setSelectedMaterial(null)}
                    onUpdate={(updated) => {
                        const newMaterials = materials.map(m =>
                            m.id === updated.id ? updated : m
                        );
                        saveMaterialsToBackend(newMaterials);
                        setSelectedMaterial(null);
                    }}
                />
            )}

            {/* Add Material Modal */}
            {showAddModal && (
                <AddMaterialModal
                    onClose={() => setShowAddModal(false)}
                    onAdd={addMaterial}
                />
            )}
                </>
            )}
        </div>
    );
}

// Material Importer Component
function MaterialImporter({ onClose, onImport }: {
    onClose: () => void;
    onImport: (materials: ExtendedMaterialSpec[]) => void;
}) {
    const [importType, setImportType] = useState<'csv' | 'json' | 'url'>('csv');
    const [url, setUrl] = useState('');
    const [jsonData, setJsonData] = useState('');
    const [importing, setImporting] = useState(false);
    const [message, setMessage] = useState('');

    const handleCSVImport = async (file: File) => {
        try {
            setImporting(true);
            const text = await file.text();
            const lines = text.split('\n').filter(line => line.trim());
            const headers = lines[0].split(',').map(h => h.trim());

            const imported: ExtendedMaterialSpec[] = [];
            for (let i = 1; i < lines.length; i++) {
                const values = lines[i].split(',');
                const material: any = {
                    id: `MAT-${Date.now()}-${i}`,
                    properties: {},
                    linkedBomIds: [],
                    certificationRequired: false
                };

                headers.forEach((header, index) => {
                    const value = values[index]?.trim();
                    if (value) {
                        if (header === 'materialName') material.materialName = value;
                        else if (header === 'grade') material.grade = value;
                        else if (header === 'standard') material.standard = value;
                        else if (header === 'supplier') material.supplier = value;
                        else material.properties[header] = value;
                    }
                });

                if (material.materialName) {
                    imported.push(material);
                }
            }

            setMessage(`✅ Successfully imported ${imported.length} materials!`);
            setTimeout(() => onImport(imported), 1500);
        } catch (error: any) {
            setMessage(`❌ Error: ${error.message}`);
        } finally {
            setImporting(false);
        }
    };

    const handleURLImport = async () => {
        try {
            setImporting(true);
            const data = await authenticatedFetchJSON(url, { skipAuth: true });

            const materials = Array.isArray(data) ? data : Object.values(data);
            setMessage(`✅ Successfully imported ${materials.length} materials!`);
            setTimeout(() => onImport(materials), 1500);
        } catch (error: any) {
            setMessage(`❌ Error: ${error.message}`);
        } finally {
            setImporting(false);
        }
    };

    const handleJSONImport = () => {
        try {
            setImporting(true);
            const data = JSON.parse(jsonData);
            const materials = Array.isArray(data) ? data : Object.values(data);

            setMessage(`✅ Successfully imported ${materials.length} materials!`);
            setTimeout(() => onImport(materials), 1500);
        } catch (error: any) {
            setMessage(`❌ Error: ${error.message}`);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-card border border-border rounded-lg max-w-2xl w-full p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-text-primary">Import Materials</h2>
                    <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Import Type Selection */}
                <div className="flex gap-2 mb-4">
                    <button
                        onClick={() => setImportType('csv')}
                        className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${importType === 'csv' ? 'bg-primary text-white' : 'bg-background hover:bg-gray-200'
                            }`}
                    >
                        <FileText className="w-4 h-4" />
                        CSV File
                    </button>
                    <button
                        onClick={() => setImportType('json')}
                        className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${importType === 'json' ? 'bg-primary text-white' : 'bg-background hover:bg-gray-200'
                            }`}
                    >
                        <FileText className="w-4 h-4" />
                        JSON
                    </button>
                    <button
                        onClick={() => setImportType('url')}
                        className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${importType === 'url' ? 'bg-primary text-white' : 'bg-background hover:bg-gray-200'
                            }`}
                    >
                        <LinkIcon className="w-4 h-4" />
                        URL
                    </button>
                </div>

                {/* Import Interface */}
                <div className="space-y-4">
                    {importType === 'csv' && (
                        <div>
                            <label className="block text-sm text-text-secondary mb-2">Select CSV File</label>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={(e) => e.target.files?.[0] && handleCSVImport(e.target.files[0])}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                disabled={importing}
                            />
                            <p className="text-xs text-text-secondary mt-2">
                                Format: materialName,grade,standard,supplier,property1,property2,...
                            </p>
                        </div>
                    )}

                    {importType === 'url' && (
                        <div>
                            <label className="block text-sm text-text-secondary mb-2">Enter JSON URL</label>
                            <input
                                type="url"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://example.com/materials.json"
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                disabled={importing}
                            />
                            <button
                                onClick={handleURLImport}
                                disabled={importing || !url}
                                className="mt-3 px-4 py-2 bg-primary hover:bg-secondary text-white rounded disabled:opacity-50"
                            >
                                {importing ? 'Importing...' : 'Import from URL'}
                            </button>
                        </div>
                    )}

                    {importType === 'json' && (
                        <div>
                            <label className="block text-sm text-text-secondary mb-2">Paste JSON Data</label>
                            <textarea
                                value={jsonData}
                                onChange={(e) => setJsonData(e.target.value)}
                                placeholder='{"id": "MAT-001", "materialName": "Steel", ...}'
                                rows={10}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none font-mono text-sm"
                                disabled={importing}
                            />
                            <button
                                onClick={handleJSONImport}
                                disabled={importing || !jsonData}
                                className="mt-3 px-4 py-2 bg-primary hover:bg-secondary text-white rounded disabled:opacity-50"
                            >
                                {importing ? 'Importing...' : 'Import JSON'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Message */}
                {message && (
                    <div className={`mt-4 p-3 rounded ${message.startsWith('✅')
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                        {message}
                    </div>
                )}
            </div>
        </div>
    );
}

// Material Detail Modal
function MaterialDetailModal({ material, onClose }: {
    material: ExtendedMaterialSpec;
    onClose: () => void;
    onUpdate: (material: ExtendedMaterialSpec) => void;
}) {
    const [formData] = useState(material);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-text-primary">{material.materialName}</h2>
                    <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm text-text-secondary mb-1">Material Name</label>
                            <div className="font-medium text-text-primary">{formData.materialName}</div>
                        </div>
                        <div>
                            <label className="block text-sm text-text-secondary mb-1">Grade</label>
                            <div className="font-medium text-text-primary">{formData.grade}</div>
                        </div>
                        <div>
                            <label className="block text-sm text-text-secondary mb-1">Standard</label>
                            <div className="font-medium text-text-primary">{formData.standard}</div>
                        </div>
                        <div>
                            <label className="block text-sm text-text-secondary mb-1">Supplier</label>
                            <div className="font-medium text-text-primary">{formData.supplier || 'N/A'}</div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm text-text-secondary mb-1">Validation Status</label>
                            <div className="font-medium text-text-primary capitalize">{formData.validationStatus || 'N/A'}</div>
                        </div>
                        {formData.cost && (
                            <div>
                                <label className="block text-sm text-text-secondary mb-1">Cost</label>
                                <div className="font-medium text-green-600">
                                    {formData.cost.value} {formData.cost.currency}
                                </div>
                            </div>
                        )}
                        {formData.leadTimeWeeks && (
                            <div>
                                <label className="block text-sm text-text-secondary mb-1">Lead Time</label>
                                <div className="font-medium text-text-primary">{formData.leadTimeWeeks} weeks</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Properties */}
                <div className="mt-6">
                    <h3 className="font-semibold text-lg text-text-primary mb-3">Properties</h3>
                    <div className="grid grid-cols-2 gap-3">
                        {Object.entries(formData.properties).map(([key, val]) => (
                            <div key={key} className="bg-background p-3 rounded border border-border">
                                <div className="text-sm text-text-secondary">{key}</div>
                                <div className="text-lg font-semibold text-text-primary">{val}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 bg-background hover:bg-gray-200 rounded transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

// Add Material Modal
function AddMaterialModal({ onClose, onAdd }: {
    onClose: () => void;
    onAdd: (material: ExtendedMaterialSpec) => void;
}) {
    const [formData, setFormData] = useState<any>({
        materialName: '',
        grade: '',
        standard: '',
        supplier: '',
        properties: {},
        linkedBomIds: [],
        certificationRequired: false
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const newMaterial: ExtendedMaterialSpec = {
            id: `MAT-${Date.now()}`,
            ...formData,
            validationStatus: 'unvalidated' as const,
            generation: 0
        };
        onAdd(newMaterial);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-card border border-border rounded-lg max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-2xl font-bold text-text-primary mb-4">Add New Material</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Material Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.materialName}
                            onChange={(e) => setFormData({ ...formData, materialName: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Grade *</label>
                        <input
                            type="text"
                            required
                            value={formData.grade}
                            onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Standard *</label>
                        <input
                            type="text"
                            required
                            value={formData.standard}
                            onChange={(e) => setFormData({ ...formData, standard: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-text-secondary mb-2">Supplier</label>
                        <input
                            type="text"
                            value={formData.supplier}
                            onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                        />
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 bg-background hover:bg-gray-200 rounded transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-primary hover:bg-secondary text-white rounded transition-colors"
                        >
                            Add Material
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
